import { useState } from "react";
import { useSnackbar } from "notistack5";
import { useTranslation } from "react-i18next";
// material
import { Box, Stack, Paper, Button, Typography } from "@material-ui/core";
import { styled, alpha } from "@material-ui/core/styles";
import { LoadingButton } from "@material-ui/lab";
// components
import Page from "../Page";
// hooks
import useAuth from "../../hooks/useAuth";
//
import { logout, sendVerificationEmail } from "../../firebaseConfig";

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
    theme.palette.primary.light,
    0.05
  )} 100%)`,
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2)
}));

const GateCard = styled(Paper)(({ theme }) => ({
  maxWidth: 480,
  width: "100%",
  padding: theme.spacing(6),
  borderRadius: theme.spacing(2),
  boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.12)}`,
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 3)
  }
}));

// ----------------------------------------------------------------------

export default function VerifyEmailGate() {
  const { t } = useTranslation();
  const { user, reloadUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);

  const handleCheck = async () => {
    setChecking(true);
    try {
      // reload() + forced token refresh — without the forced refresh the app
      // (and the server, which reads email_verified from the ID token) keep
      // seeing the stale `false` for up to an hour after the link is clicked.
      const verified = await reloadUser();
      if (!verified) {
        enqueueSnackbar(t("auth.verify.stillNotVerified"), { variant: "warning" });
      }
      // If verified, the guard re-renders into the dashboard automatically.
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t("auth.errors.generic"), { variant: "error" });
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await sendVerificationEmail();
      enqueueSnackbar(t("auth.verify.sent"), { variant: "success" });
    } catch (err: any) {
      const msg =
        err?.code === "auth/too-many-requests"
          ? t("auth.errors.tooManyRequests")
          : t("auth.errors.generic");
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setResending(false);
    }
  };

  return (
    <RootStyle title={t("auth.verify.title")}>
      <GateCard elevation={0}>
        <Stack spacing={3} alignItems="center">
          <Box component="img" src="/static/_haim/webot.png" alt="Webot" sx={{ height: 18 }} />

          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {t("auth.verify.title")}
          </Typography>

          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("auth.verify.description", { email: user?.email })}
          </Typography>

          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            loading={checking}
            onClick={handleCheck}
          >
            {t("auth.verify.iVerified")}
          </LoadingButton>

          <LoadingButton
            fullWidth
            size="large"
            variant="outlined"
            loading={resending}
            onClick={handleResend}
          >
            {t("auth.verify.resend")}
          </LoadingButton>

          <Button variant="text" color="inherit" onClick={() => logout()}>
            {t("auth.verify.useDifferentAccount")}
          </Button>
        </Stack>
      </GateCard>
    </RootStyle>
  );
}
